import React, { useCallback } from 'react';
import I18n from 'i18n-js';
import gql from 'graphql-tag';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { MutationFn } from 'react-apollo';
import { useAdyenCard } from './adyen_card_hook';
import { AdyenCardType } from '../lib/adyen';
import {
  CreditCardCreateFormFields,
  CoreCreditCardWalletCreateMyCreditCard,
  Input_core_apimessages_CreateMyCreditCardRequest as CreateMyCreditCardRequest,
  core_apimessages_AddressEntry as AddressEntry,
  core_apimessages_Country as Country,
} from '../schemas/rql';
import { flowRight } from 'lodash';
import { useMutation } from '../lib/use_mutation_hook';
import { useCreditCardForm } from './credit_card_wallet_modal';
import { withRouter, WithRouterProps } from 'react-router';

import { RCCheckbox } from '@reverbdotcom/commons/src/rc/forms';
import CreditCardEncryptedInput from './credit_card_encrypted_input';
import CreditCardEncryptedDateInput from './credit_card_encrypted_date_input';
import AddressFormFields from './address_form_fields';
import { RCAlertBox } from '@reverbdotcom/commons/src/rc/RCAlertBox';
import LoadingOverlay from '../shared/loading_overlay';
import AddressSummary from './address_summary';

type CreateMyCreditCardMutationFn = MutationFn<CoreCreditCardWalletCreateMyCreditCard.Mutation, CoreCreditCardWalletCreateMyCreditCard.Variables>;
type Me = CreditCardCreateFormFields.Fragment;

interface MutationProps {
  createMyCreditCard: CreateMyCreditCardMutationFn;
}

interface ExternalProps {
  me: Me;
  countries: Country[];
}

type Props = ExternalProps & MutationProps & WithRouterProps;

export function CreditCardCreateForm(props: Props) {
  const form = useCreditCardForm();
  const { selectedShippingAddressId } = form;

  const [isBillingSameAsShipping, setBillingSameAsShipping] = React.useState(true);
  const [billingAddress, setBillingAddress] = React.useState<AddressEntry>(shippingAddressOrDefault(props.me, selectedShippingAddressId));

  const shippingAddress = shippingAddressOrDefault(props.me, selectedShippingAddressId);
  const { regionName, isComplete } = props.me?.shippingAddresses
    .find(address => address.id === selectedShippingAddressId);

  const { cardState, focus, errors: adyenErrors } = useAdyenCard({
    selector: '[data-card-fields]',
    cardType: AdyenCardType.Card,
  });

  const { paymentMethod } = cardState.data;

  const { createMyCreditCard, loading, errors } = useCreateMyCreditCard(
    props.createMyCreditCard,
  );

  function onToggleBillingSameAsShipping() {
    if (isBillingSameAsShipping) {
      setBillingAddress(emptyAddressEntry);
    } else {
      setBillingAddress(shippingAddress);
    }

    setBillingSameAsShipping(previous => !previous);
  }

  async function onSubmit(e) {
    e.preventDefault();

    const input = {
      creditCard: {
        cardholderName: billingAddress.name,
        tokenizedFields: {
          encryptedExpiryMonth: paymentMethod.encryptedExpiryMonth,
          encryptedExpiryYear: paymentMethod.encryptedExpiryYear,
          encryptedCardNumber: paymentMethod.encryptedCardNumber,
          encryptedSecurityCode: paymentMethod.encryptedSecurityCode,
        },
      },
      billingAddress,
    };

    const result = await createMyCreditCard(input);
    const newCreditCardId = result.data?.createMyCreditCard?.creditCard?.id;

    if (newCreditCardId) {
      form.onSelect(newCreditCardId);
    }
  }

  return (
    <form id={form.id} onSubmit={onSubmit}>
      <LoadingOverlay showOverlay={loading}>
        <div data-card-fields className="g-container">
          {errors.length > 0 && (
            <div className="mb-1">
              <RCAlertBox type="error">
                {errors.map(e => <li key={`${e.field}-${e.message}`}>{e.message}</li>)}
              </RCAlertBox>
            </div>
          )}
          <div className="g-col-12 scaling-mb-2">
            <CreditCardEncryptedInput
              inputName="cardNumber"
              inputValue={paymentMethod.encryptedCardNumber}
              cseName="encryptedCardNumber"
              label={I18n.t('discovery.creditCardForm.cardNumber')}
              errors={adyenErrors}
              focus={focus}
            />
          </div>
          <div className="g-col-6 g-col-mobile-12 scaling-mb-2">
            <CreditCardEncryptedDateInput
              expiryMonthInputName="expiryMonth"
              expiryYearInputName="expiryYear"
              paymentMethod={paymentMethod}
              label={I18n.t('discovery.creditCardForm.expiration')}
              errors={adyenErrors}
              focus={focus}
            />
          </div>
          <div className="g-col-6 g-col-mobile-12 scaling-mb-2">
            <CreditCardEncryptedInput
              inputName="securityCode"
              inputValue={paymentMethod.encryptedSecurityCode}
              label={I18n.t('discovery.creditCardForm.cvv')}
              cseName="encryptedSecurityCode"
              errors={adyenErrors}
              focus={focus}
            />
          </div>
        </div>
        <div className="d-flex fx-dir-col">
          <div className="mt-1">
            <RCCheckbox
              id="credit-card-create-billing"
              name="credit-card-create-billing"
              label={I18n.t('discovery.creditCardWallet.modal.addNew.billingSame')}
              checked={isBillingSameAsShipping}
              onChange={onToggleBillingSameAsShipping}
            />
            {isBillingSameAsShipping && isComplete &&
              <div className="mt-1 ml-3 size-90">
                <AddressSummary
                  address={{
                    name: shippingAddress.name,
                    streetAddress: shippingAddress.streetAddress,
                    extendedAddress: shippingAddress.extendedAddress,
                    locality: shippingAddress.locality,
                    regionName: regionName,
                    postalCode: shippingAddress.postalCode,
                    phone: shippingAddress.phone,
                  }}
                />
              </div>
            }
          </div>
        </div>
        {!isBillingSameAsShipping &&
          <>
            <div className="bdt-1 bd-color-primary mtb-2" />
            <h3 className="weight-bold mtb-2 size-120">{I18n.t('discovery.creditCardWallet.modal.addNew.billingAddressTitle')}</h3>
            <AddressFormFields
              address={billingAddress}
              countries={props.countries}
              updateField={setBillingAddress}
              requiredFieldsOverrides={['phone']}
              useCamelizedFieldsForRQL
            />
          </>
        }
      </LoadingOverlay>
    </form>
  );
}

function shippingAddressOrDefault(me: Me, selectedShippingAddressId: string) {
  const shippingAddresses = me?.shippingAddresses || [];
  const shippingAddress = shippingAddresses.find(address => address.id === selectedShippingAddressId);

  if (shippingAddress) {
    return {
      countryCode: shippingAddress.country.countryCode,
      name: shippingAddress.name,
      streetAddress: shippingAddress.streetAddress,
      extendedAddress: shippingAddress.extendedAddress,
      locality: shippingAddress.locality,
      region: shippingAddress.region,
      postalCode: shippingAddress.postalCode,
      phone: shippingAddress.phone,
    };
  }

  return emptyAddressEntry;
}

const emptyAddressEntry = {
  countryCode: '',
  name: '',
  streetAddress: '',
  extendedAddress: '',
  locality: '',
  region: '',
  postalCode: '',
  phone: '',
};

export const CreditCardCreateFormFragment = gql`
  fragment CreditCardCreateFormFields on rql_Me {
    shippingAddresses {
      id
      uuid
      name
      streetAddress
      extendedAddress
      postalCode
      region
      regionName
      locality
      phone
      primary
      isComplete
      country {
        countryCode
      }
    }
  }
`;

export function useCreateMyCreditCard(
  createMyCreditCard: CreateMyCreditCardMutationFn,
) {
  const [mutate, { loading, errors }] = useMutation(createMyCreditCard);

  const createMyCreditCardCallback = useCallback(async (input: CreateMyCreditCardRequest) => {
    return mutate({
      variables: {
        input: {
          ...input,
        },
      },
    });
  }, [mutate]);

  return { createMyCreditCard: createMyCreditCardCallback, loading, errors };
}

const withMutation = withGraphql<Props, CoreCreditCardWalletCreateMyCreditCard.Mutation, CoreCreditCardWalletCreateMyCreditCard.Variables>(
  gql`
    mutation Core_CreditCardWallet_CreateMyCreditCard(
      $input: Input_core_apimessages_CreateMyCreditCardRequest
    ) {
      createMyCreditCard(input: $input) {
        creditCard {
          id
        }
      }
    }
  `,
  {
    name: 'createMyCreditCard',
  },
);

const connected: React.FunctionComponent<ExternalProps> = flowRight([
  withRouter,
  withMutation,
])(CreditCardCreateForm);

export default connected;
